<script setup>
// app/javascript/dashboard/routes/dashboard/products/pages/components/ImportSheetModal.vue
import { ref, computed, watch, markRaw } from 'vue';
import { useStore } from 'vuex';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootAPI from '@/v3/api/apiClient';
import ImportSheetInitial from './ImportSheetInitial.vue';
import ImportSheetPreview from './ImportSheetPreview.vue';
import ImportResult from './ImportResult.vue';
import AiColumnMapper from './AiColumnMapper.vue';
import SpreadsheetProcessor from '../../services/SpreadsheetProcessorService';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'import-products', 'import-cancel', 'success']);

// Store setup
const store = useStore();

// Computed property for formatted inbox metadata
const formattedInboxMetadata = computed(() => store.getters['inboxMetadata/getFormattedInboxes']);

const fileInput = ref(null);
const selectedRegistrationType = ref('');
const csvFile = ref(null);
const parsedCsvRows = ref([]);
const csvHeaders = ref([]);
const filteredCsvHeaders = ref([]);
const filteredCsvRows = ref([]);
const hasProductTypeColumn = ref(false);
const firstRowProductType = ref(null);
// State machine for import flow
const currentStep = ref('initial'); // 'initial' | 'mapping' | 'preview' | 'importing' | 'result'
const flowType = ref('manual'); // 'manual' | 'ai'

// Core state
const loading = ref(false);
const error = ref(null);
const importProgress = ref(0);
const totalProducts = ref(0);
const successCount = ref(0);
const errorCount = ref(0);
const resultType = ref(''); // 'success', 'error', 'warning'
const errorMessage = ref(''); // Custom error message for fatal/warning errors
const abortController = ref(null);
const selectedInboxes = ref(['todos']);
const inboxOptions = ref([]);
const showInboxDropdown = ref(false);
const productTypeValidationErrors = ref([]);
const showProductTypeDropdown = ref(false);

// Helper function to get inbox display name
const getInboxDisplayName = (inbox) => {
  // First try to get display_name from store
  const displayName = store.getters['inboxMetadata/getInboxDisplayName'](inbox.inbox_id || inbox.id);
  if (displayName) return displayName;
  
  // Fallback to inbox_name or name
  return inbox.inbox_name || inbox.name || `Inbox ${inbox.inbox_id || inbox.id}`;
};
const showRegistrationTypeSelect = ref(false);

// AI Mapping related
const selectedProductTypeForAi = ref('');
const aiMappingResult = ref(null);
const aiAbortController = ref(null);

// Simplified without caches for better maintainability

// Using strict template matching - no header mappings needed

// Template refs for dropdown positioning
const registrationTypeTrigger = ref(null);
const inboxTrigger = ref(null);
const registrationTypeDropdown = ref(null);
const inboxDropdown = ref(null);
const productTypeTrigger = ref(null);
const productTypeDropdown = ref(null);

// Utility function for dropdown positioning
const getDropdownStyles = (triggerRef, showRef, dropdownRef, offset = 2) => {
  if (!triggerRef.value || !showRef.value || !dropdownRef.value) {
    return {};
  }
  
  const triggerRect = triggerRef.value.getBoundingClientRect();
  const modalRect = dropdownRef.value.parentElement.getBoundingClientRect();
  const dropdownHeight = dropdownRef.value.clientHeight;
  
  // Calculate positions for both above and below
  const belowTop = (triggerRect.bottom - modalRect.top) + offset;
  const aboveTop = (triggerRect.top - modalRect.top) - dropdownHeight - offset;
  
  // Check if dropdown would be cut off by viewport bottom
  const dropdownBottom = triggerRect.bottom + dropdownHeight + offset;
  const viewportBottom = window.innerHeight;
  const wouldBeCutOff = dropdownBottom > viewportBottom;
  
  // Check if there's enough space above
  const hasSpaceAbove = triggerRect.top > dropdownHeight + offset;
  
  // Only show above if it would be cut off below AND there's space above
  const showAbove = wouldBeCutOff && hasSpaceAbove;
  
  // For inbox dropdown (fixed position), use viewport coordinates
  // For other dropdowns (absolute position), use modal-relative coordinates
  const isInboxDropdown = dropdownRef.value === inboxDropdown.value;
  
  const leftPosition = isInboxDropdown 
    ? `${triggerRect.left}px`
    : `${triggerRect.left - modalRect.left}px`;
  
  // For fixed positioned dropdown, use viewport coordinates for top as well
  const topPosition = isInboxDropdown
    ? `${showAbove ? triggerRect.top - dropdownHeight - offset : triggerRect.bottom + offset}px`
    : `${showAbove ? aboveTop : belowTop}px`;
  
  return {
    left: leftPosition,
    top: topPosition,
    minWidth: '200px',
    width: isInboxDropdown ? `${triggerRect.width}px` : undefined
  };
};

// Computed styles for teleported dropdowns
const registrationTypeDropdownStyles = computed(() => 
  getDropdownStyles(registrationTypeTrigger, showRegistrationTypeSelect, registrationTypeDropdown)
);

const productTypeDropdownStyles = computed(() => 
  getDropdownStyles(productTypeTrigger, showProductTypeDropdown, productTypeDropdown)
);

const inboxDropdownStyles = computed(() => 
  getDropdownStyles(inboxTrigger, showInboxDropdown, inboxDropdown)
);

// Registration types options
const registrationTypes = [
  { value: 'digital', label: 'Produto Digital', icon: 'i-lucide-smartphone' },
  { value: 'physical', label: 'Produto Físico', icon: 'i-lucide-package' },
  { value: 'aluguel', label: 'Aluguel', icon: 'i-lucide-home' },
  { value: 'assinatura', label: 'Assinatura', icon: 'i-lucide-refresh-cw' },
  { value: 'consulta', label: 'Consulta', icon: 'i-lucide-stethoscope' },
  { value: 'delivery', label: 'Delivery', icon: 'i-lucide-truck' },
  { value: 'tratamento', label: 'Tratamento', icon: 'i-lucide-heart-pulse' }
];

// Get product types from service
const validProductTypes = SpreadsheetProcessor.validProductTypes;
const productTypeLabels = SpreadsheetProcessor.productTypeLabels;

// Use service for column filtering
const filterValidColumns = (headers, rows) => {
  return SpreadsheetProcessor.filterValidColumns(headers, rows);
};

// Use service for product type validation
const validateProductTypes = (csvData) => {
  return SpreadsheetProcessor.validateProductTypes(csvData);
};



// Use service for product row processing
const processProductRow = (row, productType, selectedInboxes) => {
  return SpreadsheetProcessor.processProductRow(row, productType, selectedInboxes);
};

// Helper function to apply AI mappings to transform CSV row structure
const applyAiMappings = (row) => {
  const mappedRow = {};
  
  // Apply AI mappings to transform the row structure
  aiMappingResult.value.mappings.forEach(mapping => {
    if (mapping.mapped_field !== 'unmapped' && 
        mapping.csv_header !== null && 
        mapping.csv_header !== '') {
      mappedRow[mapping.mapped_field] = row[mapping.csv_header];
    }
  });
  
  return mappedRow;
};

// Use service for validation and formatting
const isValidRow = (row) => {
  return SpreadsheetProcessor.isValidRow(row);
};

// Validate images in parsed data to reject base64 format
const validateImagesInParsedData = (rows) => {
  for (const row of rows) {
    // Check multiple possible image column names
    const imagesValue = row.images || row.image || row.image_urls || row.imagens || row.foto || row.fotos || '';
    if (imagesValue) {
      // Handle both string (CSV) and array formats
      const images = typeof imagesValue === 'string'
        ? imagesValue.split(',').map(i => i.trim())
        : Array.isArray(imagesValue) ? imagesValue : [];

      // Check each image URL for base64 format
      for (const img of images) {
        if (img && img.toString().trim().startsWith('data:')) {
          throw new Error('Formato de imagem inválido encontrado (Base 64). Por favor, utilize links de imagens válidas.');
        }
      }
    }
  }
};

// Reset function - consolidated state reset
const resetToInitial = () => {
  // Reset state machine
  currentStep.value = 'initial';
  flowType.value = 'manual';
  
  // Reset core data
  parsedCsvRows.value = [];
  csvHeaders.value = [];
  filteredCsvHeaders.value = [];
  filteredCsvRows.value = [];
  hasProductTypeColumn.value = false;
  firstRowProductType.value = null;
  error.value = null;
  errorMessage.value = '';
  importProgress.value = 0;
  totalProducts.value = 0;
  successCount.value = 0;
  errorCount.value = 0;
  productTypeValidationErrors.value = [];
  
  // No caches to clear
  
  // Reset AI mapping related state
  selectedProductTypeForAi.value = '';
  aiMappingResult.value = null;
  
  // Clean up AI abort controller
  if (aiAbortController.value) {
    aiAbortController.value.abort();
    aiAbortController.value = null;
  }
};

// Watch para a prop show, para resetar o estado quando o modal é fechado
watch(() => props.show, (newValue) => {
  if (!newValue) {
    // Reset states quando o modal é fechado
    resetToInitial();
  } else {
    loadInboxes(); // Load inbox data when modal opens
  }
}, { immediate: true });



const successModalMessage = computed(() => {
  return `${successCount.value} cadastros importados com sucesso${errorCount.value > 0 ? `, ${errorCount.value} falhas` : ''}. Os cadastros já estão disponíveis no sistema.`;
});

// Check for missing required columns in preview
const missingColumnsWarning = computed(() => {
  if (currentStep.value !== 'preview' || !filteredCsvHeaders.value.length) return null;
  
  const requiredColumns = ['name', 'description'];
  const csvHeadersLower = filteredCsvHeaders.value.map(h => h.toLowerCase());
  const missingColumns = requiredColumns.filter(col => !csvHeadersLower.includes(col.toLowerCase()));
  
  if (missingColumns.length > 0) {
    const columnList = missingColumns.map(col => `"${col}"`).join(', ');
    return `Colunas obrigatórias ausentes: ${columnList}. A importação não poderá ser realizada.`;
  }
  
  return null;
});

// Check for invalid product types in preview
const productTypeValidationWarning = computed(() => {
  if (currentStep.value !== 'preview' || !productTypeValidationErrors.value.length) return null;
  
  const errorCount = productTypeValidationErrors.value.length;
  const firstError = productTypeValidationErrors.value[0];
  
  if (errorCount === 1) {
    return `Tipo de produto inválido na linha ${firstError.row}: "${firstError.value}".`;
  } else {
    const rows = productTypeValidationErrors.value.map(err => err.row).join(', ');
    return `${errorCount} tipos de produto inválidos nas linhas: ${rows}.`;
  }
});

// Check for rows filtered out due to missing required fields
const filteredRowsWarning = computed(() => {
  if (currentStep.value !== 'preview' || !parsedCsvRows.value.length) return null;
  
  const totalRows = parsedCsvRows.value.length;
  const validRows = parsedCsvRows.value.filter(row => {
    // Same validation as in importCSVData
    if (!row.name) return false;
    if (!row.description) return false;
    return true;
  });
  
  const filteredOutCount = totalRows - validRows.length;
  
  if (filteredOutCount > 0) {
    return `${filteredOutCount} linha${filteredOutCount !== 1 ? 's' : ''} ser${filteredOutCount !== 1 ? 'ão' : 'á'} ignorada${filteredOutCount !== 1 ? 's' : ''} por falta de campos obrigatórios (nome e descrição).`;
  }
  
  return null;
});

// Generate import summary
const importSummary = computed(() => {
  if (currentStep.value !== 'preview' || !filteredCsvRows.value.length) return null;
  
  // Don't show summary if there are blocking validation errors
  if (missingColumnsWarning.value || productTypeValidationWarning.value) return null;
  
  // Count only valid rows that would actually be imported (same logic as import)
  const validRows = parsedCsvRows.value.filter(row => {
    // Same validation as in importCSVData
    if (!row.name) return false;
    if (!row.description) return false;
    return true;
  });
  
  const totalRows = validRows.length;
  
  // Determine product type
  let productTypeLabel = 'Produtos';
  if (hasProductTypeColumn.value && firstRowProductType.value) {
    const typeKey = firstRowProductType.value.toLowerCase();
    productTypeLabel = productTypeLabels[typeKey] || 'Produtos';
  } else {
    // Use auto-detection on first row to determine type
    const firstRow = parsedCsvRows.value[0];
    if (firstRow) {
      const detectedType = detectRegistrationType(firstRow);
      productTypeLabel = productTypeLabels[detectedType] || 'Produtos';
    }
  }
  
  // Determine inbox name
  let inboxName = 'todos os inboxes';
  if (selectedInboxes.value.includes('nenhum')) {
    inboxName = 'nenhum inbox';
  } else if (!selectedInboxes.value.includes('todos') && selectedInboxes.value.length > 0) {
    if (selectedInboxes.value.length === 1) {
      const inbox = inboxOptions.value.find(inbox => 
        String(inbox.inbox_id) === selectedInboxes.value[0]
      );
      inboxName = inbox ? getInboxDisplayName(inbox) : 'inbox selecionado';
    } else {
      inboxName = `${selectedInboxes.value.length} inboxes selecionados`;
    }
  }
  
  return `Serão importados ${totalRows} ${productTypeLabel.toLowerCase()} no seu catálogo para ${inboxName}.`;
});

// Functions auth
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) {
    return {};
  }
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      'client': sessionInfo['client'],
      'uid': sessionInfo['uid']
    };
  } catch (e) {
    return {};
  }
};

// Updated processCsvFile function to handle both CSV and XLSX
const processCsvFile = (file, isAiFlow = false) => {
  return new Promise((resolve, reject) => {
    const fileExtension = file.name.toLowerCase().split('.').pop();
    
    if (fileExtension === 'xlsx') {
      // Handle XLSX files
      const reader = new FileReader();
      reader.onload = () => {
        try {
          // Parse XLSX file
          const data = new Uint8Array(reader.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1,
            defval: '', // Default value for empty cells
            raw: false // Convert everything to strings initially
          });
          
          // Use service to process XLSX data
          const { headers, parsedData } = SpreadsheetProcessor.processXlsxData(jsonData);
          
          // Process the same way as CSV
          if (isAiFlow) {
            const { headers: filteredHeaders, rows: filteredRows } =
              filterValidColumns(headers, parsedData);

            csvHeaders.value = filteredHeaders;
            parsedCsvRows.value = filteredRows;

            // Validate images for base64 format
            validateImagesInParsedData(parsedData);

            resolve({
              headers: filteredHeaders,
              rows: filteredRows,
              sampleRows: filteredRows.slice(0, 10)
            });
          } else {
            csvHeaders.value = headers;
            parsedCsvRows.value = parsedData;
            
            const productTypeIndex = headers.findIndex(field => 
              field.toLowerCase() === 'product_type'
            );
            
            if (productTypeIndex !== -1) {
              hasProductTypeColumn.value = true;
              if (parsedData.length > 0) {
                firstRowProductType.value = parsedData[0].product_type;
              }
              
              productTypeValidationErrors.value = validateProductTypes(parsedData);
              
              const headersWithoutProductType = headers.filter(field => 
                field.toLowerCase() !== 'product_type'
              );
              const rowsWithoutProductType = parsedData.map(row => {
                const filteredRow = { ...row };
                delete filteredRow.product_type;
                return filteredRow;
              });
              
              const filtered = filterValidColumns(headersWithoutProductType, rowsWithoutProductType);
              filteredCsvHeaders.value = filtered.headers;
              filteredCsvRows.value = filtered.rows;
            } else {
              hasProductTypeColumn.value = false;
              firstRowProductType.value = null;
              productTypeValidationErrors.value = [];

              const filtered = filterValidColumns(headers, parsedData);
              filteredCsvHeaders.value = filtered.headers;
              filteredCsvRows.value = filtered.rows;
            }

            // Validate images for base64 format
            validateImagesInParsedData(parsedData);

            resolve({
              headers: filteredCsvHeaders.value,
              rows: filteredCsvRows.value
            });
          }
        } catch (error) {
          reject(new Error(`Erro ao processar planilha: ${error.message}`));
        }
      };
      
      reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
      reader.readAsArrayBuffer(file);
      
    } else if (fileExtension === 'csv') {
      // Handle CSV files (existing logic)
      const reader = new FileReader();
      reader.onload = () => {
        const csvData = reader.result;
        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim().toLowerCase(),
          complete: (results) => {
            try {
              if (isAiFlow) {
                const { headers: filteredHeaders, rows: filteredRows } =
                  filterValidColumns(results.meta.fields, results.data);

                csvHeaders.value = filteredHeaders;
                parsedCsvRows.value = filteredRows;

                // Validate images for base64 format
                validateImagesInParsedData(results.data);

                resolve({
                  headers: filteredHeaders,
                  rows: filteredRows,
                  sampleRows: filteredRows.slice(0, 10)
                });
              } else {
                csvHeaders.value = results.meta.fields;
                parsedCsvRows.value = results.data;
                
                const productTypeIndex = results.meta.fields.findIndex(field => 
                  field.toLowerCase() === 'product_type'
                );
                
                if (productTypeIndex !== -1) {
                  hasProductTypeColumn.value = true;
                  if (results.data.length > 0) {
                    firstRowProductType.value = results.data[0].product_type;
                  }
                  
                  productTypeValidationErrors.value = validateProductTypes(results.data);
                  
                  const headersWithoutProductType = results.meta.fields.filter(field => 
                    field.toLowerCase() !== 'product_type'
                  );
                  const rowsWithoutProductType = results.data.map(row => {
                    const filteredRow = { ...row };
                    delete filteredRow.product_type;
                    return filteredRow;
                  });
                  
                  const filtered = filterValidColumns(headersWithoutProductType, rowsWithoutProductType);
                  filteredCsvHeaders.value = filtered.headers;
                  filteredCsvRows.value = filtered.rows;
                } else {
                  hasProductTypeColumn.value = false;
                  firstRowProductType.value = null;
                  productTypeValidationErrors.value = [];

                  const filtered = filterValidColumns(results.meta.fields, results.data);
                  filteredCsvHeaders.value = filtered.headers;
                  filteredCsvRows.value = filtered.rows;
                }

                // Validate images for base64 format
                validateImagesInParsedData(results.data);

                resolve({
                  headers: filteredCsvHeaders.value,
                  rows: filteredCsvRows.value
                });
              }
            } catch (error) {
              reject(error);
            }
          },
          error: (error) => reject(new Error(`Erro ao processar CSV: ${error.message}`))
        });
      };
      reader.readAsText(file);
      
    } else {
      reject(new Error('Formato de arquivo não suportado. Use apenas .csv ou .xlsx'));
    }
  });
};

// Methods
const triggerFileSelect = () => {
  fileInput.value?.click();
};

const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file type
  const fileExtension = file.name.toLowerCase().split('.').pop();
  if (!['csv', 'xlsx'].includes(fileExtension)) {
    error.value = 'Formato de arquivo não suportado. Use apenas arquivos .csv ou .xlsx';
    return;
  }

  csvFile.value = file;

  try {
    await processCsvFile(file, false);
    
    // Update state machine
    currentStep.value = 'preview';
    flowType.value = 'manual';
    
    // Reset file input
    csvFile.value = null;
    event.target.value = null;
  } catch (err) {
    console.error('Error processing file:', err);
    error.value = err.message || 'Erro ao processar arquivo';
  }
};

const handleFileDropped = async (file, errorMessage) => {
  // Handle error from drag and drop validation
  if (errorMessage) {
    error.value = errorMessage;
    return;
  }
  
  if (!file) return;

  csvFile.value = file;

  try {
    await processCsvFile(file, false);
    
    // Update state machine
    currentStep.value = 'preview';
    flowType.value = 'manual';
    
    // Reset file
    csvFile.value = null;
  } catch (err) {
    console.error('Error processing file:', err);
    error.value = err.message || 'Erro ao processar arquivo';
  }
};


const importCSVData = async () => {
  // Reset states
  error.value = null;
  loading.value = true;
  currentStep.value = 'importing';
  importProgress.value = 0;
  successCount.value = 0;
  errorCount.value = 0;
  
  // Create new AbortController for this import session
  abortController.value = new AbortController();
  
  // Get auth headers and verify user is logged in
  const headers = getAuthHeaders();
  if (!headers['access-token']) {
    loading.value = false;
    resultType.value = 'error';
    errorMessage.value = 'Não foi possível completar a importação devido a um erro interno. Tente novamente mais tarde.';
    currentStep.value = 'result';
    return;
  }

  // Filter out invalid rows early before expensive processing
  const validRows = parsedCsvRows.value.filter(isValidRow);
  
  const mappedProducts = validRows
    .map(row => {
      // Determine product type - use each row's product_type if column exists, otherwise auto-detect
      let detectedType;
      if (hasProductTypeColumn.value && row.product_type) {
        // Use the row's own product_type value when column exists
        detectedType = row.product_type.toString().toLowerCase().trim();
      } else {
        // Auto-detect only when no product_type column exists
        detectedType = detectRegistrationType(row);
      }
      
      // Use central processing function
      return processProductRow(row, detectedType, selectedInboxes.value);
    })
    .filter(Boolean);

  totalProducts.value = mappedProducts.length;
  
  if (mappedProducts.length === 0) {
    loading.value = false;
    resultType.value = 'error';
    errorMessage.value = `Não foi possível completar a importação de ${parsedCsvRows.value.length} cadastros, a planilha contém dados incorretos. Verifique e tente novamente.`;
    currentStep.value = 'result';
    return;
  }

  const importedProducts = [];
  
  // Make batch API request for all products
  try {
    
    const response = await wootAPI.post(
      `/api/v1/catalogs/batch_import`,
      { products: mappedProducts },
      { headers, signal: abortController.value.signal }
    );
    
    // Handle batch response
    if (response.data.status === 'completed') {
      const summary = response.data.summary;
      successCount.value = summary.successful_count;
      errorCount.value = summary.failed_count;
      
      // Add successful products to importedProducts for parent component
      if (summary.successful_products) {
        importedProducts.push(...summary.successful_products);
      }
      
      importProgress.value = 100;
    } else {
      throw new Error(response.data.message || 'Erro desconhecido na importação em lote');
    }
    
    // Emit the imported products to the parent component
    emit('import-products', importedProducts);
    
    // Show result container based on import results
    loading.value = false;
    
    if (successCount.value > 0 && errorCount.value > 0) {
      // Partial success - show warning
      resultType.value = 'warning';
      errorMessage.value = `${errorCount.value} cadastros contém valores inválidos. Outros ${successCount.value} cadastros foram importados com sucesso.`;
    } else if (successCount.value > 0 && errorCount.value === 0) {
      // Complete success
      resultType.value = 'success';
    } else {
      // Complete failure
      resultType.value = 'error';
      errorMessage.value = `Não foi possível completar a importação de ${totalProducts.value} cadastros devido a um erro interno. Tente novamente mais tarde.`;
    }
    
    currentStep.value = 'result';
  } catch (err) {
    // Handle aborted requests
    if (err.name === 'AbortError' || err.name === 'CanceledError') {
      console.log('Batch import was aborted by user');
      return;
    }
    
    console.error('Erro durante a importação em lote:', err);
    loading.value = false;
    resultType.value = 'error';
    
    // Try to get a more specific error message from the response
    if (err.response?.data?.message) {
      errorMessage.value = err.response.data.message;
    } else {
      errorMessage.value = `Não foi possível completar a importação de ${totalProducts.value} cadastros devido a um erro interno. Tente novamente mais tarde.`;
    }
    
    currentStep.value = 'result';
  }
};

const closeModal = () => {
  // Abort any in-flight AI requests
  if (aiAbortController.value) {
    aiAbortController.value.abort();
    aiAbortController.value = null;
  }
  
  resetToInitial();
  selectedRegistrationType.value = '';
  selectedInboxes.value = ['todos'];
  emit('close');
};

const openRegistrationTypeDropdown = (event) => {
  if (event && event.currentTarget) {
    registrationTypeTrigger.value = event.currentTarget;
  }
  showRegistrationTypeSelect.value = true;
};

const closeRegistrationTypeSelect = () => {
  showRegistrationTypeSelect.value = false;
};

const openProductTypeDropdown = (event) => {
  if (event && event.currentTarget) {
    productTypeTrigger.value = event.currentTarget;
  }
  showProductTypeDropdown.value = true;
};

const closeProductTypeDropdown = () => {
  showProductTypeDropdown.value = false;
};

// Use service for registration type detection
const detectRegistrationType = (row) => {
  return SpreadsheetProcessor.detectRegistrationType(row);
};

// CSV Template Generator based on CatalogModal.vue fields
const generateCSVTemplate = (registrationType) => {
  const baseHeaders = ['name', 'description', 'price', 'discountPrice', 'discountReason', 'category', 'images'];
  const baseExample = ['Nome do Produto', 'Descrição detalhada', '99.90', '79.90', 'Desconto promocional', 'Categoria', 'https://exemplo.com/imagem.jpg'];
  
  const typeSpecific = {
    digital: {
      headers: ['contextUrl', 'fileFormat', 'licenseType'],
      example: ['https://exemplo.com/curso', 'MP4, PDF', 'Licença vitalícia']
    },
    physical: {
      headers: ['contextUrl', 'weightKg', 'height_cm', 'width_cm', 'depth_cm', 'color', 'shipsTo'],
      example: ['https://loja.com/produto', '0.180', '15.5', '7.5', '0.8', 'Preto, Azul', 'Todo o Brasil']
    },
    delivery: {
      headers: ['link', 'address', 'maxDistance', 'isFixedPrice', 'estimatedMinutes'],
      example: ['https://delivery.com', 'Rua das Flores, 123', 'Birigui, Araçatuba', 'false', '30']
    },
    consulta: {
      headers: ['product_type', 'link', 'address', 'isHourlyRate', 'advanceBookingDays', 'specialty', 'durationMinutes'],
      example: ['consulta', 'https://clinica.com', 'Av. Saúde, 456', 'false', '3', 'Cardiologia', '60']
    },
    tratamento: {
      headers: ['product_type', 'link', 'address', 'isHourlyRate', 'advanceBookingDays', 'specialty', 'durationMinutes'],
      example: ['tratamento', 'https://fisio.com', 'Rua Reabilitação, 789', 'false', '1', 'Ortopedia', '45']
    },
    aluguel: {
      headers: ['link', 'address', 'isHourlyRate', 'advanceBookingDays'],
      example: ['https://salao.com', 'Av. Eventos, 321', 'false', '7']
    },
    assinatura: {
      headers: ['link', 'address', 'planType'],
      example: ['https://streaming.com', '', 'Mensal']
    }
  };
  
  const specific = typeSpecific[registrationType];
  if (!specific) return null;
  
  return {
    headers: [...baseHeaders, ...specific.headers],
    example: [...baseExample, ...specific.example]
  };
};

// CSV File Download Function
const downloadCSVTemplate = (registrationType) => {
  const template = generateCSVTemplate(registrationType);
  if (!template) {
    console.error('Template não encontrado para:', registrationType);
    return;
  }
  
  // Create CSV content with proper escaping
  const csvContent = [
    template.headers.join(','),
    template.example.map(field => {
      // Escape fields that contain commas, quotes, or newlines
      if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    }).join(',')
  ].join('\n');
  
  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `template_${registrationType}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Enhanced template generation function for both CSV and XLSX
const generateTemplate = (registrationType, format = 'csv') => {
  const template = generateCSVTemplate(registrationType);
  if (!template) return null;
  
  if (format === 'xlsx') {
    // Create XLSX template
    const ws = XLSX.utils.aoa_to_sheet([
      template.headers,
      template.example
    ]);
    
    // Set column widths for better readability
    const colWidths = template.headers.map(() => ({ wch: 20 }));
    ws['!cols'] = colWidths;
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    
    // Generate and download XLSX file
    XLSX.writeFile(wb, `template_${registrationType}.xlsx`);
  } else {
    // Generate CSV (existing logic)
    downloadCSVTemplate(registrationType);
  }
};

const selectRegistrationType = (type) => {
  selectedRegistrationType.value = type;
  closeRegistrationTypeSelect();
  // Download only XLSX format
  generateTemplate(type, 'xlsx');
};

const handleResultContainerClose = () => {
  // Emit success event to trigger loadProducts() in parent
  if (resultType.value === 'success') {
    emit('success');
  }
  // Close the modal
  closeModal();
};

const handleImportCancel = () => {
  loading.value = false;
  
  // Abort any in-flight HTTP requests - this stops everything
  if (abortController.value) {
    abortController.value.abort();
  }
  
  // Reset to initial state
  resetToInitial();
  
  // Emit import-cancel event to trigger data refresh in parent
  emit('import-cancel');
  
  // Close the modal
  emit('close');
};

// Inbox-related functions
const loadInboxes = () => {
  try {
    // Get inbox metadata from Vuex store
    const inboxMetadata = formattedInboxMetadata.value;
    
    // Filter for connected inboxes only
    const connectedInboxes = (inboxMetadata.results || []).filter(inbox => {
      // Check if any instance has connectionStatus === 2
      return inbox.instances && inbox.instances.some(instance => 
        instance.connectionStatus === 2
      );
    });
    
    inboxOptions.value = connectedInboxes;
    console.log('Loaded inboxes from Vuex store:', inboxOptions.value.length);
  } catch (err) {
    console.error('Falha ao carregar inboxes:', err);
    inboxOptions.value = [];
  }
};

const toggleInboxDropdown = (event) => {
  if (event && event.currentTarget) {
    inboxTrigger.value = event.currentTarget;
  }
  showInboxDropdown.value = !showInboxDropdown.value;
};

const closeInboxDropdown = () => {
  showInboxDropdown.value = false;
};

const toggleInboxSelection = (inboxId) => {
  const currentSelection = [...selectedInboxes.value];
  
  if (inboxId === 'todos') {
    // If selecting "Todos", clear all individual selections
    if (currentSelection.includes('todos')) {
      // If "Todos" is already selected, deselect it
      selectedInboxes.value = [];
    } else {
      // Select "Todos" and clear individual selections
      selectedInboxes.value = ['todos'];
    }
  } else if (inboxId === 'nenhum') {
    // If selecting "Nenhum inbox", clear all other selections
    if (currentSelection.includes('nenhum')) {
      // If "Nenhum" is already selected, deselect it
      selectedInboxes.value = [];
    } else {
      // Select "Nenhum" and clear all other selections
      selectedInboxes.value = ['nenhum'];
    }
  } else {
    // Individual inbox selection
    const todosIndex = currentSelection.indexOf('todos');
    const nenhumIndex = currentSelection.indexOf('nenhum');
    const inboxIndex = currentSelection.indexOf(inboxId);
    
    // Remove "Todos" and "Nenhum" if they were selected (since we're selecting specific inboxes)
    if (todosIndex !== -1) {
      currentSelection.splice(todosIndex, 1);
    }
    if (nenhumIndex !== -1) {
      currentSelection.splice(nenhumIndex, 1);
    }
    
    if (inboxIndex === -1) {
      // Add inbox to selection
      currentSelection.push(inboxId);
    } else {
      // Remove inbox from selection
      currentSelection.splice(inboxIndex, 1);
    }
    
    selectedInboxes.value = currentSelection;
  }
};

const isInboxSelected = (inboxId) => {
  return selectedInboxes.value.includes(inboxId);
};

// Grouped inbox options similar to CatalogModal
const groupedInboxOptions = computed(() => {
  const result = [];
  const inboxes = inboxOptions.value || [];
  
  // Group inboxes by type
  const whatsappEvolution = [];
  const whatsappOfficial = [];
  const otherChannels = [];
  
  inboxes.forEach(inbox => {
    const name = (inbox.inbox_name || inbox.name || '').toLowerCase();
    
    if (inbox.integration_type === 'official' || 
        name.includes('api oficial') ||
        name.includes('cloud') ||
        name.includes('official')) {
      whatsappOfficial.push({...inbox, integration_type: 'official'});
    }
    else if (inbox.integration_type === 'evolution' || name.includes('evolution')) {
      whatsappEvolution.push({...inbox, integration_type: 'evolution'});
    }
    else {
      otherChannels.push(inbox);
    }
  });
  
  // Add WhatsApp Official section
  if (whatsappOfficial.length > 0) {
    result.push({ type: 'header', label: 'WhatsApp Business API' });
    result.push(...whatsappOfficial);
  }
  
  // Add WhatsApp Evolution section
  if (whatsappEvolution.length > 0) {
    result.push({ type: 'header', label: 'WhatsApp Evolution API' });
    result.push(...whatsappEvolution);
  }
  
  // Add Other Channels section
  if (otherChannels.length > 0) {
    result.push({ type: 'header', label: 'Outros Canais' });
    result.push(...otherChannels);
  }
  
  return result;
});




const backToInitialScreen = () => {
  // Abort any in-flight AI requests
  if (aiAbortController.value) {
    aiAbortController.value.abort();
    aiAbortController.value = null;
  }
  
  resetToInitial();
};

const proceedWithImport = () => {
  importCSVData();
};

// AI Mapping Flow Methods
const triggerGerarPlanilha = () => {
  currentStep.value = 'mapping';
  flowType.value = 'ai';
};

const selectProductTypeForAi = (productType) => {
  selectedProductTypeForAi.value = productType;
  closeProductTypeDropdown();
  // Don't hide the selector yet - wait for file selection
  triggerFileSelectForAi();
};

const triggerFileSelectForAi = () => {
  // Create a temporary file input for AI flow
  const tempFileInput = document.createElement('input');
  tempFileInput.type = 'file';
  tempFileInput.accept = '.csv,.xlsx';
  tempFileInput.addEventListener('change', handleAiFileUpload);
  tempFileInput.click();
};

const handleAiFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Validate file type
  const fileExtension = file.name.toLowerCase().split('.').pop();
  if (!['csv', 'xlsx'].includes(fileExtension)) {
    errorMessage.value = 'Formato de arquivo não suportado. Use apenas arquivos .csv ou .xlsx';
    currentStep.value = 'result';
    resultType.value = 'error';
    return;
  }

  csvFile.value = file;
  loading.value = true;
  error.value = null;

  try {
    const result = await processCsvFile(file, true);
    
    // Update state machine
    currentStep.value = 'mapping';
    flowType.value = 'ai';
    
    // Create AbortController for AI requests
    aiAbortController.value = new AbortController();
    
    // Call AI analysis API with filtered data
    const headers = getAuthHeaders();
    const response = await wootAPI.post(
      '/api/v1/catalogs/analyze_csv',
      {
        csv_headers: result.headers,
        sample_rows: result.sampleRows,
        product_type: selectedProductTypeForAi.value
      },
      { 
        headers,
        timeout: 60000,
        signal: aiAbortController.value.signal
      }
    );
    
    if (response.data.status === 'success') {
      aiMappingResult.value = response.data.mapping_analysis;
    } else {
      throw new Error(response.data.error || 'AI analysis failed');
    }
  } catch (err) {
    // Check if the request was cancelled
    if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
      // Request was cancelled, just reset without showing error
      console.log('AI analysis cancelled by user');
      loading.value = false;
      return;
    }
    
    console.error('AI analysis error:', err);
    
    if (err.code === 'ECONNABORTED' || err.response?.status === 500) {
      errorMessage.value = 'A análise demorou mais que o esperado. Verifique sua conexão e tente novamente.';
    } else {
      errorMessage.value = err.message || 'Houve um erro interno durante o mapeamento. Tente novamente ou envie um modelo pronto.';
    }
    
    currentStep.value = 'result';
    resultType.value = 'error';
  } finally {
    loading.value = false;
    aiAbortController.value = null;
  }
};

// Retry AI analysis function
const retryAiAnalysis = () => {
  // Reset error state  
  error.value = null;
  errorMessage.value = '';
  
  // Start analyzing again
  currentStep.value = 'mapping';
  flowType.value = 'ai';
  
  // Re-trigger the AI analysis with the same file
  if (csvFile.value) {
    const fakeEvent = {
      target: {
        files: [csvFile.value]
      }
    };
    handleAiFileUpload(fakeEvent);
  }
};

// Check if all required fields are mapped by AI
const areRequiredFieldsMapped = computed(() => {
  if (!aiMappingResult.value) return false;
  
  const requiredFields = ['name', 'description', 'price'];
  const mappedFields = aiMappingResult.value.mappings
    .filter(m => m.mapped_field !== 'unmapped' && m.csv_header !== null && m.csv_header !== '')
    .map(m => m.mapped_field);
    
  return requiredFields.every(field => mappedFields.includes(field));
});



const applyMappingsAndProceed = () => {
  // Check if original data has a product_type column
  const originalHasProductType = parsedCsvRows.value.some(row => 
    row.hasOwnProperty('product_type') && row.product_type
  );
  
  // Filter out invalid rows early before expensive processing
  const validRows = parsedCsvRows.value.filter(row => {
    const mappedRow = applyAiMappings(row);
    // If original data had product_type, preserve it in the mapped row
    if (originalHasProductType && row.product_type) {
      mappedRow.product_type = row.product_type;
    }
    return isValidRow(mappedRow);
  });
  
  // Transform CSV data using AI mappings (but don't process into product objects yet)
  // Let the regular import flow handle the product processing
  const mappedRows = validRows.map(row => {
    // Apply AI mappings to transform row structure
    const mappedRow = applyAiMappings(row);
    
    // Preserve original product_type if it exists
    if (originalHasProductType && row.product_type) {
      mappedRow.product_type = row.product_type;
    } else if (!mappedRow.product_type) {
      // Only add selected product type if no product_type exists
      mappedRow.product_type = selectedProductTypeForAi.value;
    }
    
    return mappedRow;
  }).filter(Boolean);
  
  // Update the data for existing import flow
  parsedCsvRows.value = mappedRows;
  csvHeaders.value = Object.keys(mappedRows[0] || {});
  filteredCsvHeaders.value = csvHeaders.value;
  filteredCsvRows.value = mappedRows;
  
  // Set product type info based on actual data, not UI selection
  if (originalHasProductType) {
    hasProductTypeColumn.value = true;
    // Use the first row's actual product_type value
    firstRowProductType.value = mappedRows[0]?.product_type || selectedProductTypeForAi.value;
  } else {
    // Only use UI selection if no product_type column exists in original data
    hasProductTypeColumn.value = true;
    firstRowProductType.value = selectedProductTypeForAi.value;
  }
  
  // Switch to preview mode
  currentStep.value = 'preview';
};

const backToProductTypeSelector = () => {
  // Abort any in-flight AI requests
  if (aiAbortController.value) {
    aiAbortController.value.abort();
    aiAbortController.value = null;
  }
  
  currentStep.value = 'mapping';
  flowType.value = 'ai';
  aiMappingResult.value = null;
};

// Dynamic component mapping
const stepComponents = computed(() => ({
  initial: markRaw(ImportSheetInitial),
  mapping: flowType.value === 'ai' ? markRaw(AiColumnMapper) : null,
  preview: markRaw(ImportSheetPreview),
  result: markRaw(ImportResult),
  importing: null // Will render inline progress
}));

// Get current component
const currentComponent = computed(() => stepComponents.value[currentStep.value]);

// Props for current step component
const currentStepProps = computed(() => {
  switch (currentStep.value) {
    case 'initial':
      return {
        error: error.value,
        selectedRegistrationType: selectedRegistrationType.value,
        registrationTypes: registrationTypes,
        showRegistrationTypeDropdown: showRegistrationTypeSelect.value
      };
    case 'mapping':
      if (flowType.value === 'ai') {
        return {
          selectedProductTypeForAi: selectedProductTypeForAi.value,
          registrationTypes: registrationTypes,
          loading: loading.value,
          aiMappingResult: aiMappingResult.value,
          csvHeaders: csvHeaders.value,
          showProductTypeDropdown: showProductTypeDropdown.value,
          errorMessage: errorMessage.value,
          showError: currentStep.value === 'result' && flowType.value === 'ai' && resultType.value === 'error'
        };
      }
      return {};
    case 'preview':
      return {
        error: error.value,
        missingColumnsWarning: missingColumnsWarning.value,
        productTypeValidationWarning: productTypeValidationWarning.value,
        filteredRowsWarning: filteredRowsWarning.value,
        importSummary: importSummary.value,
        filteredCsvHeaders: filteredCsvHeaders.value,
        filteredCsvRows: filteredCsvRows.value,
        selectedInboxes: selectedInboxes.value,
        inboxOptions: inboxOptions.value,
        showInboxDropdown: showInboxDropdown.value
      };
    case 'result':
      return {
        resultType: resultType.value,
        successModalMessage: successModalMessage.value,
        errorMessage: errorMessage.value
      };
    default:
      return {};
  }
});



// Update mapping when user changes dropdown selection
const updateFieldMapping = (templateFieldKey, selectedCsvHeader) => {
  if (!aiMappingResult.value) return;
  
  // Find the existing mapping for this template field
  const existingMapping = aiMappingResult.value.mappings.find(m => m.mapped_field === templateFieldKey);
  
  if (existingMapping) {
    // Update existing mapping
    if (selectedCsvHeader === 'unmapped') {
      existingMapping.mapped_field = 'unmapped';
      existingMapping.csv_header = '';
      existingMapping.confidence = 0;
      existingMapping.user_modified = true; // Mark as user modified
    } else {
      existingMapping.csv_header = selectedCsvHeader;
      existingMapping.confidence = 1.0; // User override has full confidence
      existingMapping.user_modified = true; // Mark as user modified
    }
  } else {
    // Create new mapping if it doesn't exist
    aiMappingResult.value.mappings.push({
      csv_header: selectedCsvHeader === 'unmapped' ? '' : selectedCsvHeader,
      mapped_field: selectedCsvHeader === 'unmapped' ? 'unmapped' : templateFieldKey,
      confidence: selectedCsvHeader === 'unmapped' ? 0 : 1.0,
      user_modified: true // Mark as user modified
    });
  }
};

// Helper function to get inbox type icon
const getInboxTypeIcon = (channelType) => {
  switch (channelType) {
    case 'Channel::WhatsApp':
      return 'i-lucide-message-circle';
    case 'Channel::Telegram':
      return 'i-lucide-send';
    case 'Channel::Email':
      return 'i-lucide-mail';
    default:
      return 'i-lucide-inbox';
  }
};




</script>

<template>
  <!-- Modal principal -->
  <Teleport to="body">
    <div v-if="show" class="fixed inset-0 bg-black/[0.04] flex items-center justify-center z-[1000] backdrop-blur-sm" @click.self="!loading && currentStep !== 'mapping' && closeModal()">
    <!-- Hidden file input -->
    <input ref="fileInput" type="file" accept=".csv,.xlsx" class="hidden" @change="handleFileUpload" />
    
    <!-- Container do modal -->
    <div class="relative bg-n-alpha-3 shadow-xl rounded-xl rtl:text-right skip-context-menu w-[48rem] max-h-[90vh] flex flex-col">
      <!-- Fixed Header -->
      <div class="flex justify-between items-center p-4 border-b-2 border-n-slate-4 dark:border-n-slate-7 flex-shrink-0">
        <h3 class="text-lg font-semibold m-0">
          <span v-if="currentStep === 'initial'">Importar Planilha de Produtos</span>
          <span v-else-if="currentStep === 'mapping' && flowType === 'ai' && !aiMappingResult">Gerar Planilha</span>
          <span v-else-if="currentStep === 'mapping' && flowType === 'ai' && aiMappingResult">Mapeamento de Colunas</span>
          <span v-else-if="currentStep === 'preview'">Pré-visualização dos Dados</span>
          <span v-else-if="currentStep === 'importing'">Importando Cadastros</span>
          <span v-else-if="currentStep === 'result'">Resultado da Importação</span>
        </h3>
        <button
          class="z-10 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
          @click="closeModal">
          <span class="i-lucide-x flex-shrink-0"></span>
        </button>
      </div>

      <!-- Scrollable Body -->
      <div class="flex-1 overflow-y-auto">
        <!-- Dynamic component rendering -->
        <component 
          v-if="currentComponent"
          :is="currentComponent"
          v-bind="currentStepProps"
          @select-registration-type="selectRegistrationType"
          @toggle-registration-dropdown="openRegistrationTypeDropdown"
          @trigger-file-select="triggerFileSelect"
          @trigger-ai-flow="triggerGerarPlanilha"
          @select-product-type="selectProductTypeForAi"
          @toggle-product-dropdown="openProductTypeDropdown"
          @update-field-mapping="updateFieldMapping"
          @retry-analysis="retryAiAnalysis"
          @toggle-inbox-dropdown="toggleInboxDropdown"
          @close-inbox-dropdown="closeInboxDropdown"
          @toggle-inbox-selection="toggleInboxSelection"
          @file-dropped="handleFileDropped"
        />

        <!-- Import progress modal (shown during import) -->
        <div v-else-if="currentStep === 'importing'" class="p-6">

        <!-- Loading body - styled like ImportContactsModal -->
        <div class="flex flex-col justify-center">
          <div class="p-6 bg-n-slate-1 dark:bg-n-slate-3 rounded-lg border border-n-slate-3 dark:border-n-slate-7">
            <div class="space-y-4">
              <!-- Importing message with spinner -->
              <div class="flex items-start">
                <Spinner class="text-woot-600 mt-1 mr-2" :size="20" />
                <div>
                  <p class="text-base font-medium text-n-slate-12">
                    Importando cadastros...
                  </p>
                  <p class="text-sm text-n-slate-11 mt-1">
                    Aguarde enquanto os cadastros são processados. Não feche esta janela durante a importação.
                  </p>
                </div>
              </div>

              <!-- Progress bar (only show when progress > 0) -->
              <div v-if="importProgress > 0" class="mt-4">
                <div class="flex justify-between text-xs text-n-slate-11 mb-1">
                  <span>Progresso: {{ importProgress }}%</span>
                  <span>{{ successCount }} importados{{ errorCount > 0 ? `, ${errorCount} falhas` : '' }}</span>
                </div>
                <div class="w-full bg-n-slate-3 dark:bg-n-slate-6 rounded-full h-2">
                  <div class="h-2 rounded-full bg-n-brand transition-all duration-300" :style="`width: ${importProgress}%`"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fixed Footer -->
      <div class="border-t-2 border-n-slate-4 dark:border-n-slate-7 p-4 sticky bottom-0 bg-n-alpha-3">
        <!-- AI Flow - Product Type Selector footer -->
        <div v-if="currentStep === 'mapping' && flowType === 'ai' && !aiMappingResult" class="flex justify-between items-center">
          <button 
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="backToInitialScreen"
          >
            <i class="i-lucide-arrow-left flex-shrink-0"></i>
            Voltar
          </button>
          <button type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="closeModal" :disabled="loading">
            Cancelar
          </button>
        </div>

        <!-- AI Flow - Analysis Loading footer -->
        <div v-else-if="currentStep === 'mapping' && flowType === 'ai' && loading" class="flex justify-end">
          <button type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="closeModal">
            Cancelar
          </button>
        </div>

        <!-- AI Flow - Error footer -->
        <div v-else-if="currentStep === 'result' && flowType === 'ai' && resultType === 'error'" class="flex justify-between items-center">
          <button 
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="backToInitialScreen"
          >
            <i class="i-lucide-arrow-left flex-shrink-0"></i>
            Voltar
          </button>
          <button 
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:bg-n-brand/90 focus-visible:bg-n-brand/90 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="retryAiAnalysis"
          >
            <i class="i-lucide-refresh-cw flex-shrink-0"></i>
            Tentar Novamente
          </button>
        </div>

        <!-- AI Flow - Mapping Interface footer -->
        <div v-else-if="currentStep === 'mapping' && flowType === 'ai' && aiMappingResult" class="flex justify-between">
          <button 
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="backToProductTypeSelector"
          >
            Voltar
          </button>
          <div class="flex gap-3">
            <button 
              type="button"
              class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button 
              type="button"
              class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:bg-n-brand/90 focus-visible:bg-n-brand/90 outline-transparent h-10 px-4 text-sm font-medium justify-center"
              @click="applyMappingsAndProceed"
              :disabled="!areRequiredFieldsMapped"
            >
              Aplicar Mapeamento
            </button>
          </div>
        </div>

        <!-- Initial screen footer -->
        <div v-else-if="currentStep === 'initial'" class="flex justify-end">
          <button type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="closeModal" :disabled="loading">
            Cancelar
          </button>
        </div>

        <!-- Preview footer -->
        <div v-else-if="currentStep === 'preview'" class="flex justify-between items-center">
          <button 
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="backToInitialScreen"
          >
            Voltar
          </button>
          <div class="flex gap-3">
            <button 
              type="button"
              class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
              @click="closeModal"
            >
              Cancelar
            </button>
            <button 
              type="button"
              class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:bg-n-brand/90 focus-visible:bg-n-brand/90 outline-transparent h-10 px-4 text-sm font-medium justify-center"
              @click="proceedWithImport"
              :disabled="!!missingColumnsWarning || !!productTypeValidationWarning"
            >
              Importar Cadastros
            </button>
          </div>
        </div>

        <!-- Loading footer -->
        <div v-else-if="loading" class="flex justify-end items-center">
          <button 
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-slate-9/10 text-n-slate-12 hover:enabled:bg-n-slate-9/20 focus-visible:bg-n-slate-9/20 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleImportCancel"
          >
            Cancelar
          </button>
        </div>

        <!-- Result footer -->
        <div v-else-if="currentStep === 'result'" class="flex justify-end">
          <button 
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:bg-n-brand/90 focus-visible:bg-n-brand/90 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            @click="handleResultContainerClose"
          >
            Finalizar
          </button>
        </div>
      </div>
    </div>
    
    <!-- Registration Type Dropdown - Teleported -->
    <div
      ref="registrationTypeDropdown"
      v-if="showRegistrationTypeSelect"
      v-on-clickaway="closeRegistrationTypeSelect"
      class="dropdown-pane dropdown-pane--open absolute bg-white dark:bg-n-slate-2 shadow-lg rounded-lg z-[10050] border border-n-slate-4 dark:border-n-slate-7"
      :style="registrationTypeDropdownStyles"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-for="type in registrationTypes" :key="type.value">
          <Button
            :label="type.label"
            ghost
            slate
            sm
            start
            :icon="type.icon"
            class="w-full"
            @click="selectRegistrationType(type.value)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    
    <!-- Product Type Dropdown - Teleported -->
    <div
      ref="productTypeDropdown"
      v-if="showProductTypeDropdown"
      v-on-clickaway="closeProductTypeDropdown"
      class="dropdown-pane dropdown-pane--open absolute bg-white dark:bg-n-slate-2 shadow-lg rounded-lg z-[10050] border border-n-slate-4 dark:border-n-slate-7"
      :style="productTypeDropdownStyles"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-for="type in registrationTypes" :key="type.value">
          <Button
            :label="type.label"
            ghost
            slate
            sm
            start
            :icon="type.icon"
            class="w-full"
            @click="selectProductTypeForAi(type.value)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    
    <!-- Inbox Dropdown - Teleported -->
    <div
      ref="inboxDropdown"
      v-if="showInboxDropdown"
      v-on-clickaway="closeInboxDropdown"
      class="fixed z-[10050] border border-n-slate-3 rounded-md shadow-lg bg-white dark:bg-slate-800 overflow-hidden"
      :style="inboxDropdownStyles"
      @click.stop
    >
      <!-- Todos option -->
      <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
           @click.stop="toggleInboxSelection('todos')">
        <div class="flex items-center">
          <input type="checkbox" 
                 :checked="isInboxSelected('todos')" 
                 @click.stop
                 @change.stop="toggleInboxSelection('todos')"
                 class="mr-3">
          <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            <i class="i-lucide-globe"></i>
          </div>
          <div>
            <div class="text-sm font-medium">Todos</div>
            <div class="text-xs text-slate-500">Disponível para todos os inboxes</div>
          </div>
        </div>
      </div>
      
      <!-- Nenhum inbox option -->
      <div class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
           @click.stop="toggleInboxSelection('nenhum')">
        <div class="flex items-center">
          <input type="checkbox" 
                 :checked="isInboxSelected('nenhum')" 
                 @click.stop
                 @change.stop="toggleInboxSelection('nenhum')"
                 class="mr-3">
          <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3 bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            <i class="i-lucide-x-circle"></i>
          </div>
          <div>
            <div class="text-sm font-medium">Nenhum inbox</div>
            <div class="text-xs text-slate-500">Produto não será enviado para nenhum inbox</div>
          </div>
        </div>
      </div>
      
      <!-- Grouped Inboxes -->
      <div class="max-h-60 overflow-y-auto">
        <template v-for="(item, index) in groupedInboxOptions" :key="index">
          <!-- Group Header -->
          <div v-if="item.type === 'header'"
               class="px-3 py-1 text-xs font-semibold bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-y border-slate-100 dark:border-slate-700 sticky top-0 z-10">
            {{ item.label }}
          </div>
          
          <!-- Inbox Option -->
          <div v-else
               class="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
               @click.stop="toggleInboxSelection(String(item.inbox_id))">
            <div class="flex items-center">
              <input type="checkbox" 
                     :checked="isInboxSelected(String(item.inbox_id))" 
                     @click.stop
                     @change.stop="toggleInboxSelection(String(item.inbox_id))"
                     class="mr-3">
              <div class="w-8 h-8 flex items-center justify-center rounded-full mr-3"
                   :class="{
                     'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400': 
                       item.integration_type === 'evolution',
                     'bg-woot-50 text-woot-600 dark:bg-woot-900/30 dark:text-woot-400': 
                       item.integration_type === 'official',
                     'bg-slate-50 text-slate-600 dark:bg-slate-700 dark:text-slate-300': 
                       !item.integration_type
                   }">
                <i :class="getInboxTypeIcon(item.channel_type)"></i>
              </div>
              
              <div>
                <div class="text-sm font-medium">
                  {{ getInboxDisplayName(item) }}
                </div>
                <div v-if="item.integration_type === 'official'" class="text-xs text-amber-600 dark:text-amber-400">
                  API Oficial
                </div>
                <div v-else-if="item.integration_type === 'evolution'" class="text-xs text-green-600 dark:text-green-400">
                  Não Oficial
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
  </div>
  </Teleport>


</template>

<style scoped>
.filter-actions {
  .dropdown-pane {
    .dropdown-menu__item {
      @apply mb-0;
    }
  }
  
  .button-group {
    &:hover {
      button {
        @apply bg-n-slate-3 dark:bg-n-slate-6;
      }
    }
    
    button {
      @apply transition-colors duration-200;
      
      &:hover {
        @apply bg-n-slate-3 dark:bg-n-slate-6;
      }
    }
  }
}
</style>